import { showTelegramAlert } from '../../services/telegramService';
import type { Language } from '../../i18n';
import { useTranslation } from '../../hooks/useTranslation';

interface CashbackInfoProps {
  exchange: {
    code: string;
    name: string;
    nameVi: string;
    cashbackInfo?: {
      exchangeCashback: number;
      exchangeCashbackLabel: string;
      exchangeCashbackLabelVi: string;
      hubCashback: number;
      hubCashbackLabel: string;
      hubCashbackLabelVi: string;
      hubCashbackType: 'exclusive' | 'normal';
      totalCashback: number;
      referralCode: string;
      registerUrl: string;
      description: string;
      descriptionVi: string;
    };
  };
  language: Language;
  onRegister: () => void;
}

const CashbackInfo = ({ exchange, onRegister }: CashbackInfoProps) => {
  const cashbackInfo = exchange.cashbackInfo;
  
  // ✅ Early return BEFORE hooks!
  if (!cashbackInfo) {
    return null;
  }
  
  // ✅ Hooks AFTER early return check
  const { t } = useTranslation();
  const exchangeName = exchange.name;
  
  const autoCashback = cashbackInfo.exchangeCashback || 0;
  const backcomCashback = cashbackInfo.hubCashback || 0;
  const totalCashback = cashbackInfo.totalCashback || 0;

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-2">
        {t('rebate.title')}
      </h2>
      <p className="text-sm text-tg-hint mb-6">
        {cashbackInfo.description || exchangeName}
      </p>

      {/* Cashback Breakdown */}
      <div className="bg-tg-secondary rounded-xl p-4 mb-4 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-white">
            {cashbackInfo.exchangeCashbackLabel}
          </span>
          <span className="text-base font-bold text-white">{autoCashback}%</span>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-white">
              {cashbackInfo.hubCashbackLabel}
            </span>
            {cashbackInfo.hubCashbackType === 'exclusive' && (
              <span className="px-2 py-0.5 bg-primary rounded text-xs font-semibold text-black">
                Exclusive
              </span>
            )}
          </div>
          <span className="text-base font-bold text-primary">{backcomCashback}%</span>
        </div>
      </div>

      {/* Total Cashback - Highlighted */}
      <div className="bg-gradient-to-r from-primary/30 to-primary/20 border border-primary rounded-xl p-4 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-white">
            {t('rebate.totalRebate')}
          </span>
          <span className="text-2xl font-bold text-primary">{totalCashback.toFixed(2)}%</span>
        </div>
      </div>

      {/* Referral Code */}
      <div className="bg-tg-secondary rounded-xl p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-white">
            Code
          </span>
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-primary">{cashbackInfo.referralCode}</span>
            <button
              onClick={() => {
                navigator.clipboard.writeText(cashbackInfo.referralCode);
                showTelegramAlert(t('common.copied'));
              }}
              className="text-tg-hint hover:text-primary transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Register Button */}
      <button
        onClick={onRegister}
        className="w-full py-4 bg-gradient-to-r from-primary to-primary/90 text-black rounded-xl text-base font-semibold flex items-center justify-center gap-2 transition-opacity active:opacity-80"
      >
        {exchangeName}
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </button>
    </div>
  );
};

export default CashbackInfo;


